import React, { useState } from "react";
import { Box, Stack, TextField, Typography, alpha } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import colors from "../../../assets/theme/colors";
import ChildPhotoUploader from "../ChildPhotoUploader";
import CareFocusSelectionFields from "./CareFocusSelectionFields";
import ProfileDateOfBirthFields from "./ProfileDateOfBirthFields";
import { splitCareFocusSelection } from "../../../utils/careFocusSelection";

const getCareFocusSummary = (selectedConditions = []) => {
  const { primaryFocus, additionalSupports } = splitCareFocusSelection(selectedConditions);
  if (!primaryFocus) return "Not set yet";
  if (!additionalSupports.length) return primaryFocus.label;
  return `${primaryFocus.label} · ${additionalSupports.length} support${additionalSupports.length === 1 ? "" : "s"}`;
};

/**
 * Shared identity fields for a care profile's "Person" editor: photo, name, date of
 * birth, pronouns, language, and care focus. Used by both the step-based intake wizard
 * (ChildProfileFlowContent) and the Care Setup → Person editor (CareProfileSectionEditor)
 * so the two flows cannot drift from each other again. Owns field rendering only —
 * step navigation, save/cancel, and Firestore writes stay in the parent flow.
 */
const PersonIdentityFields = ({
  nameLabel = "Preferred name",
  name,
  setName,
  dateOfBirth,
  onDateOfBirthChange,
  fallbackAgeGroup = "",
  pronouns,
  setPronouns,
  language,
  setLanguage,
  setPhoto,
  photoURL,
  setPhotoURL,
  selectedConditions = [],
  setSelectedConditions,
  fieldSx = {},
  compact = true,
  showPhoto = true,
}) => {
  const [isCareFocusExpanded, setIsCareFocusExpanded] = useState(false);
  const careFocusSummary = getCareFocusSummary(selectedConditions);

  return (
    <Stack spacing={compact ? 1.2 : 1.35}>
      {showPhoto ? (
        <ChildPhotoUploader
          setPhoto={setPhoto}
          photoURL={photoURL}
          setPhotoURL={setPhotoURL}
          label="Profile photo"
          compactRow
        />
      ) : null}
      <TextField
        label={`${nameLabel} *`}
        variant="outlined"
        fullWidth
        value={name || ""}
        onChange={(event) => setName?.(event.target.value)}
        sx={fieldSx}
      />
      <ProfileDateOfBirthFields
        dateOfBirth={dateOfBirth}
        onDateOfBirthChange={onDateOfBirthChange}
        fallbackAgeGroup={fallbackAgeGroup}
        fieldSx={fieldSx}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
        <TextField
          label="Pronouns"
          variant="outlined"
          fullWidth
          value={pronouns || ""}
          onChange={(event) => setPronouns?.(event.target.value)}
          placeholder="she/her"
          sx={fieldSx}
        />
        <TextField
          label="Language"
          variant="outlined"
          fullWidth
          value={language || ""}
          onChange={(event) => setLanguage?.(event.target.value)}
          placeholder="English"
          sx={fieldSx}
        />
      </Stack>
      <Box
        sx={{
          borderRadius: "16px",
          border: "1px solid",
          borderColor: isCareFocusExpanded ? alpha(colors.brand.deep, 0.18) : "transparent",
          bgcolor: alpha(colors.brand.deep, 0.06),
          overflow: "hidden",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setIsCareFocusExpanded((current) => !current)}
          aria-expanded={isCareFocusExpanded}
          sx={{
            width: "100%",
            p: 1.1,
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr) auto",
            gap: 1,
            alignItems: "center",
            border: "none",
            bgcolor: "transparent",
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
          }}
        >
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: colors.semantic.profileFlow.cardBg, color: colors.brand.deep }}>
            <ExploreOutlinedIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 850, lineHeight: 1.2 }}>
              Care focus *
            </Typography>
            <Typography sx={{ mt: 0.15, fontSize: "0.82rem", color: colors.semantic.ink2 }}>
              {careFocusSummary}
            </Typography>
          </Box>
          <ChevronRightRoundedIcon sx={{ fontSize: 20, color: colors.brand.deep, transform: isCareFocusExpanded ? "rotate(90deg)" : "none" }} />
        </Box>
        {isCareFocusExpanded ? (
          <Box
            sx={{
              px: 1.4,
              pt: 1.2,
              pb: 1.4,
              borderTop: "1px solid",
              borderColor: alpha(colors.brand.deep, 0.14),
              bgcolor: colors.semantic.profileFlow.cardBg,
            }}
          >
            <CareFocusSelectionFields
              value={selectedConditions}
              onChange={setSelectedConditions}
              fieldSx={fieldSx}
              stackSpacing={1.4}
            />
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
};

export default PersonIdentityFields;
